import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import Loading from "@/components/Common/Loading";
import Page from "@/components/Common/Page";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { callApi, default as query } from "@/Utils/request/query";

import {
  MVPModule,
  MVPModuleConfiguration,
  MVPModuleConfigurationWrite,
} from "@/types/mvp/operations";
import mvpOperationsApi from "@/types/mvp/operationsApi";

type MVPCountResponse = { count?: number; results: unknown[] };

const countRecords = (response?: MVPCountResponse) =>
  response?.count ?? response?.results.length ?? 0;

export function MVPModuleOperations({ facilityId }: { facilityId: string }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const moduleConfigurationsQuery = useQuery({
    queryKey: ["mvp-module-configurations", facilityId],
    queryFn: query(mvpOperationsApi.listModuleConfigurations, {
      pathParams: { facilityId },
    }),
  });
  const storesQuery = useQuery({
    queryKey: ["mvp-pharmacy-stores", facilityId],
    queryFn: query(mvpOperationsApi.listPharmacyStores, {
      pathParams: { facilityId },
    }),
  });
  const drugsQuery = useQuery({
    queryKey: ["mvp-pharmacy-drugs", facilityId],
    queryFn: query(mvpOperationsApi.listPharmacyDrugs, {
      pathParams: { facilityId },
    }),
  });
  const laboratoryTestsQuery = useQuery({
    queryKey: ["mvp-laboratory-tests", facilityId],
    queryFn: query(mvpOperationsApi.listLaboratoryTests, {
      pathParams: { facilityId },
    }),
  });
  const emergencyRegistrationsQuery = useQuery({
    queryKey: ["mvp-emergency-registrations", facilityId],
    queryFn: query(mvpOperationsApi.listEmergencyRegistrations, {
      pathParams: { facilityId },
    }),
  });
  const emergencyAssessmentsQuery = useQuery({
    queryKey: ["mvp-emergency-assessments", facilityId],
    queryFn: query(mvpOperationsApi.listEmergencyAssessments, {
      pathParams: { facilityId },
    }),
  });
  const emergencyBedAllocationsQuery = useQuery({
    queryKey: ["mvp-emergency-bed-allocations", facilityId],
    queryFn: query(mvpOperationsApi.listEmergencyBedAllocations, {
      pathParams: { facilityId },
    }),
  });
  const billingPaymentsQuery = useQuery({
    queryKey: ["mvp-billing-payments", facilityId],
    queryFn: query(mvpOperationsApi.listBillingPayments, {
      pathParams: { facilityId },
    }),
  });
  const imagingStudiesQuery = useQuery({
    queryKey: ["mvp-imaging-studies", facilityId],
    queryFn: query(mvpOperationsApi.listImagingStudies, {
      pathParams: { facilityId },
    }),
  });

  const toggleModule = useMutation({
    mutationFn: ({
      module,
      configuration,
    }: {
      module: MVPModule;
      configuration?: MVPModuleConfiguration;
    }) => {
      const body: MVPModuleConfigurationWrite = {
        module,
        enabled: !configuration?.enabled,
        settings: configuration?.settings ?? {},
      };
      return configuration
        ? callApi(mvpOperationsApi.updateModuleConfiguration, {
            pathParams: { facilityId, configurationId: configuration.id },
            body,
          })
        : callApi(mvpOperationsApi.createModuleConfiguration, {
            pathParams: { facilityId },
            body,
          });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["mvp-module-configurations", facilityId],
      });
    },
  });

  const configurations = moduleConfigurationsQuery.data?.results ?? [];
  const moduleDefinitions: Array<{
    key: MVPModule;
    label: string;
    description: string;
  }> = [
    {
      key: "pharmacy",
      label: t("mvp_pharmacy", { defaultValue: "Pharmacy" }),
      description: t("mvp_pharmacy_description", {
        defaultValue: "Stores, drug masters, batches, and cold-chain controls.",
      }),
    },
    {
      key: "laboratory",
      label: t("mvp_laboratory", { defaultValue: "Laboratory" }),
      description: t("mvp_laboratory_description", {
        defaultValue:
          "Specimen, test, reference-range, and critical-value setup.",
      }),
    },
    {
      key: "emergency",
      label: t("mvp_emergency", { defaultValue: "Emergency" }),
      description: t("mvp_emergency_description", {
        defaultValue: "Rapid registration, walk-in queue, triage, and alerts.",
      }),
    },
  ];

  if (
    moduleConfigurationsQuery.isLoading ||
    storesQuery.isLoading ||
    drugsQuery.isLoading ||
    laboratoryTestsQuery.isLoading ||
    emergencyRegistrationsQuery.isLoading ||
    emergencyAssessmentsQuery.isLoading ||
    emergencyBedAllocationsQuery.isLoading ||
    billingPaymentsQuery.isLoading ||
    imagingStudiesQuery.isLoading
  ) {
    return <Loading />;
  }

  return (
    <Page
      title={t("mvp_module_operations", {
        defaultValue: "MVP module operations",
      })}
    >
      <div className="mx-auto mt-8 grid w-full max-w-5xl gap-6">
        <Card>
          <CardHeader>
            <CardTitle>
              {t("mvp_module_configuration", {
                defaultValue: "Module configuration",
              })}
            </CardTitle>
            <CardDescription>
              {t("mvp_module_configuration_description", {
                defaultValue:
                  "Enable only the facility workflows your operational team is ready to use.",
              })}
            </CardDescription>
          </CardHeader>
          <CardContent className="grid gap-4 md:grid-cols-3">
            {moduleDefinitions.map((moduleDefinition) => {
              const configuration = configurations.find(
                (item) => item.module === moduleDefinition.key,
              );
              const enabled = configuration?.enabled ?? false;
              return (
                <div
                  className="grid gap-3 rounded-lg border p-4"
                  key={moduleDefinition.key}
                >
                  <div>
                    <h3 className="font-medium">{moduleDefinition.label}</h3>
                    <p className="mt-1 text-sm text-gray-500">
                      {moduleDefinition.description}
                    </p>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-sm">
                      {enabled
                        ? t("mvp_enabled", { defaultValue: "Enabled" })
                        : t("mvp_disabled", { defaultValue: "Disabled" })}
                    </span>
                    <Button
                      disabled={toggleModule.isPending}
                      onClick={() =>
                        toggleModule.mutate({
                          module: moduleDefinition.key,
                          configuration,
                        })
                      }
                      type="button"
                      variant={enabled ? "outline" : "default"}
                    >
                      {enabled
                        ? t("mvp_disable", { defaultValue: "Disable" })
                        : t("mvp_enable", { defaultValue: "Enable" })}
                    </Button>
                  </div>
                </div>
              );
            })}
          </CardContent>
          {toggleModule.isError && (
            <p className="px-6 pb-5 text-sm text-red-600" role="alert">
              {t("mvp_module_update_error", {
                defaultValue:
                  "Module configuration could not be updated. Check your facility permission and the server validation message.",
              })}
            </p>
          )}
        </Card>

        <div className="grid gap-4 md:grid-cols-3">
          <Card>
            <CardHeader>
              <CardTitle>
                {t("mvp_pharmacy_summary", { defaultValue: "Pharmacy" })}
              </CardTitle>
            </CardHeader>
            <CardContent className="grid gap-1 text-sm">
              <span>
                {t("mvp_store_count", { defaultValue: "Stores" })}:{" "}
                {countRecords(storesQuery.data)}
              </span>
              <span>
                {t("mvp_drug_count", { defaultValue: "Drug masters" })}:{" "}
                {countRecords(drugsQuery.data)}
              </span>
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle>
                {t("mvp_laboratory_summary", { defaultValue: "Laboratory" })}
              </CardTitle>
            </CardHeader>
            <CardContent className="text-sm">
              {t("mvp_test_count", { defaultValue: "Test masters" })}:{" "}
              {countRecords(laboratoryTestsQuery.data)}
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle>
                {t("mvp_emergency_summary", {
                  defaultValue: "Emergency queue",
                })}
              </CardTitle>
            </CardHeader>
            <CardContent className="text-sm">
              {t("mvp_registration_count", { defaultValue: "Registrations" })}:{" "}
              {countRecords(emergencyRegistrationsQuery.data)}
              <div>
                {t("mvp_assessment_count", { defaultValue: "Assessments" })}:{" "}
                {countRecords(emergencyAssessmentsQuery.data)}
              </div>
              <div>
                {t("mvp_bed_count", { defaultValue: "Bed allocations" })}:{" "}
                {countRecords(emergencyBedAllocationsQuery.data)}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle>
                {t("mvp_payment_ledger", { defaultValue: "Payment ledger" })}
              </CardTitle>
            </CardHeader>
            <CardContent className="text-sm">
              {t("mvp_payment_count", { defaultValue: "Payments" })}:{" "}
              {countRecords(billingPaymentsQuery.data)}
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle>
                {t("mvp_imaging_summary", { defaultValue: "Imaging" })}
              </CardTitle>
            </CardHeader>
            <CardContent className="text-sm">
              {t("mvp_imaging_count", { defaultValue: "Imaging studies" })}:{" "}
              {countRecords(imagingStudiesQuery.data)}
            </CardContent>
          </Card>
        </div>
      </div>
    </Page>
  );
}
